import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ExternalLink, BookOpen, CheckCircle, Heart } from "lucide-react";
import SEO from "@/components/SEO";
import { usePageSeo } from "@/hooks/usePageSeo";

const BookPage = () => {
  const seoOverride = usePageSeo("book");
  const bookFeatures = [
    "An 11 Weeks Journey To Go Deep Within",
    "Learn all about anxiety, its symptoms, and how it shows up in your life",
    "Learn how to identify when you've been triggered",
    "Learn skills that can help you take your power back from anxiety",
    "Learn how to identify and manage your racing thoughts",
    "Find out where your negative thoughts come from and how they've been impacting your life",
  ];

  return (
    <div className="pt-20" data-testid="book-page">
      <SEO
        title="Worry Less & Beat That Anxiety — Book by Laura A. Jeanis"
        metaTitle={seoOverride?.meta_title}
        keywords={[seoOverride?.focus_keyword, ...(seoOverride?.secondary_keywords || [])]}
        description={seoOverride?.meta_description || "An 11-week journey to break free from anxiety. Discover Laura A. Jeanis's book 'Worry Less' — practical tools, deep insights, and lasting transformation."}
        imageAlt={seoOverride?.image_alt || "Worry Less & Beat That Anxiety! book cover by Laura A. Jeanis"}
        pathname="/book"
        image="https://customer-assets.emergentagent.com/job_therapy-solutions-1/artifacts/uovh4d4y_71RrJ%2BxoLfL._SL1500_.jpg"
      />
      {/* Hero Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Book Cover */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="flex justify-center lg:justify-end"
            >
              <a 
                href="https://a.co/d/07TEq4qE" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block transform hover:scale-105 transition-transform duration-300 cursor-pointer"
                data-testid="book-cover-link"
              >
                <div className="relative">
                  <img
                    src="https://customer-assets.emergentagent.com/job_therapy-solutions-1/artifacts/unwyel7v_IMG_2280.jpeg"
                    alt="Worry Less & Beat That Anxiety! by Laura A. Jeanis"
                    className="w-full max-w-sm rounded-2xl shadow-hover"
                  />
                  <div className="absolute inset-0 bg-black/0 hover:bg-black/10 rounded-2xl transition-colors duration-300 flex items-center justify-center opacity-0 hover:opacity-100">
                    <span className="bg-white/90 text-primary px-4 py-2 rounded-full font-medium flex items-center gap-2">
                      <ExternalLink className="h-4 w-4" />
                      Buy on Amazon
                    </span>
                  </div>
                </div>
              </a>
            </motion.div>

            {/* Book Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="h-5 w-5 text-accent" />
                <span className="text-accent font-medium">New Book Release</span>
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-foreground leading-tight">
                Worry Less & Beat That Anxiety!
              </h1>
              <p className="mt-2 text-lg text-primary font-medium">
                By Laura A. Jeanis, LCSW, LICSW, CCTP, CEO
              </p>
              
              <div className="mt-8">
                <a 
                  href="https://a.co/d/07TEq4qE" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button 
                    size="lg" 
                    className="rounded-full px-10 py-6 text-lg btn-scale shadow-soft bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="buy-book-btn"
                  >
                    <ExternalLink className="mr-2 h-5 w-5" />
                    Get Your Copy on Amazon
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Book Description */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl font-semibold text-foreground mb-8">
              About This Book
            </h2>
            
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                In full transparency, I got tired of anxiety whooping my behind and wrote this book/workbook as a result.
              </p>
              <p>
                I'm gonna be honest, even as a psychotherapist, I could not just snap my fingers and make the anxiety disappear.
              </p>
              <p className="text-foreground font-medium">
                If you are tired of anxiety, racing thoughts, triggers that you can't pinpoint, and old childhood experiences invading your peace, then get this book!
              </p>
              <p>
                Listen, it's like this. I've been through severe trauma at an early age, and I've had to experience severe anxiety as a "bonus". I have found a way to manage it, and I want to share that with you.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl font-semibold text-foreground">
              What You're Getting
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bookFeatures.map((feature) => (
              <motion.div
                key={feature}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                viewport={{ once: true }}
              >
                <Card className="p-5 bg-white border-0 rounded-xl flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-foreground">{feature}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Personal Message */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Heart className="h-12 w-12 text-accent mx-auto mb-6" />
            <blockquote className="font-serif text-2xl sm:text-3xl text-foreground italic leading-relaxed">
              "If this book has come into your life, do not ignore it. I've set the intention for this book to find those who need it the most. If you're reading this, that person is you."
            </blockquote>
            <p className="mt-8 text-xl text-primary font-medium">
              Happy Journeying Within! One-Love.
            </p>
            <p className="mt-2 text-muted-foreground">
              — Laura A. Jeanis
            </p>

            <div className="mt-12">
              <a 
                href="https://a.co/d/07TEq4qE" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg" 
                  className="rounded-full px-10 py-6 text-lg btn-scale shadow-soft bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="buy-book-btn-bottom"
                >
                  <ExternalLink className="mr-2 h-5 w-5" />
                  Get Your Copy on Amazon
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BookPage;
